import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, SlidersHorizontal, MapPin, Briefcase, DollarSign, X } from 'lucide-react';
import { jobs } from '@/data/jobs';
import { jobCategories, locations, salaryRanges } from '@/lib/index';
import { JobCard } from '@/components/JobCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

export default function Jobs() {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedSalaryRanges, setSelectedSalaryRanges] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const jobTypes = [
    { value: 'full-time', label: '全职' },
    { value: 'part-time', label: '兼职' },
    { value: 'contract', label: '合同' },
    { value: 'internship', label: '实习' },
  ];

  const toggleCategory = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const toggleLocation = (location: string) => {
    setSelectedLocations((prev) =>
      prev.includes(location)
        ? prev.filter((l) => l !== location)
        : [...prev, location]
    );
  };

  const toggleSalaryRange = (range: string) => {
    setSelectedSalaryRanges((prev) =>
      prev.includes(range)
        ? prev.filter((r) => r !== range)
        : [...prev, range]
    );
  };

  const toggleType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const clearAllFilters = () => {
    setSearchKeyword('');
    setSelectedCategories([]);
    setSelectedLocations([]);
    setSelectedSalaryRanges([]);
    setSelectedTypes([]);
  };

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const matchesKeyword =
        !searchKeyword ||
        job.title.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        job.company.name.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        job.tags.some((tag) =>
          tag.toLowerCase().includes(searchKeyword.toLowerCase())
        );

      const matchesCategory =
        selectedCategories.length === 0 ||
        selectedCategories.includes(job.category);

      const matchesLocation =
        selectedLocations.length === 0 ||
        selectedLocations.includes(job.location);

      const matchesType =
        selectedTypes.length === 0 || selectedTypes.includes(job.type);

      const matchesSalary =
        selectedSalaryRanges.length === 0 ||
        selectedSalaryRanges.some((range) => {
          const [min, max] = range.split('-').map((v) => (v === '+' ? Infinity : parseInt(v)));
          return job.salary.min >= min && (max === Infinity || job.salary.max <= max);
        });

      return (
        matchesKeyword &&
        matchesCategory &&
        matchesLocation &&
        matchesType &&
        matchesSalary
      );
    });
  }, [searchKeyword, selectedCategories, selectedLocations, selectedSalaryRanges, selectedTypes]);

  const activeFiltersCount =
    selectedCategories.length +
    selectedLocations.length +
    selectedSalaryRanges.length +
    selectedTypes.length;

  const FilterSection = () => (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Briefcase className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-foreground">职位分类</h3>
        </div>
        <div className="space-y-3">
          {jobCategories.map((category: { value: string; label: string }) => (
            <div key={category.value} className="flex items-center space-x-2">
              <Checkbox
                id={`category-${category.value}`}
                checked={selectedCategories.includes(category.value)}
                onCheckedChange={() => toggleCategory(category.value)}
              />
              <Label
                htmlFor={`category-${category.value}`}
                className="text-sm font-normal cursor-pointer"
              >
                {category.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      <div>
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-foreground">工作地点</h3>
        </div>
        <div className="space-y-3">
          {locations.map((location: string) => (
            <div key={location} className="flex items-center space-x-2">
              <Checkbox
                id={`location-${location}`}
                checked={selectedLocations.includes(location)}
                onCheckedChange={() => toggleLocation(location)}
              />
              <Label
                htmlFor={`location-${location}`}
                className="text-sm font-normal cursor-pointer"
              >
                {location}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      <div>
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-foreground">薪资范围</h3>
        </div>
        <div className="space-y-3">
          {salaryRanges.map((range: { value: string; label: string }) => (
            <div key={range.value} className="flex items-center space-x-2">
              <Checkbox
                id={`salary-${range.value}`}
                checked={selectedSalaryRanges.includes(range.value)}
                onCheckedChange={() => toggleSalaryRange(range.value)}
              />
              <Label
                htmlFor={`salary-${range.value}`}
                className="text-sm font-normal cursor-pointer"
              >
                {range.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      <div>
        <div className="flex items-center gap-2 mb-4">
          <Briefcase className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-foreground">工作类型</h3>
        </div>
        <div className="space-y-3">
          {jobTypes.map((type) => (
            <div key={type.value} className="flex items-center space-x-2">
              <Checkbox
                id={`type-${type.value}`}
                checked={selectedTypes.includes(type.value)}
                onCheckedChange={() => toggleType(type.value)}
              />
              <Label
                htmlFor={`type-${type.value}`}
                className="text-sm font-normal cursor-pointer"
              >
                {type.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {activeFiltersCount > 0 && (
        <Button
          variant="outline"
          className="w-full"
          onClick={clearAllFilters}
        >
          <X className="w-4 h-4 mr-2" />
          清除所有筛选
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-4">职位列表</h1>
          <p className="text-muted-foreground text-lg">
            发现适合你的职位机会,开启职业新篇章
          </p>
        </div>

        <div className="mb-6">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="搜索职位、公司或技能..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="pl-10 h-12"
              />
            </div>
            <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="lg:hidden h-12 px-4">
                  <SlidersHorizontal className="w-5 h-5 mr-2" />
                  筛选
                  {activeFiltersCount > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80">
                <SheetHeader>
                  <SheetTitle>筛选条件</SheetTitle>
                </SheetHeader>
                <ScrollArea className="h-[calc(100vh-8rem)] mt-6">
                  <FilterSection />
                </ScrollArea>
              </SheetContent>
            </Sheet>
          </div>

          {activeFiltersCount > 0 && (
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <span className="text-sm text-muted-foreground">已选筛选：</span>
              {selectedCategories.map((cat) => (
                <Badge key={cat} variant="secondary" className="gap-1">
                  {jobCategories.find((c: { value: string; label: string }) => c.value === cat)?.label}
                  <X
                    className="w-3 h-3 cursor-pointer"
                    onClick={() => toggleCategory(cat)}
                  />
                </Badge>
              ))}
              {selectedLocations.map((loc) => (
                <Badge key={loc} variant="secondary" className="gap-1">
                  {loc}
                  <X
                    className="w-3 h-3 cursor-pointer"
                    onClick={() => toggleLocation(loc)}
                  />
                </Badge>
              ))}
              {selectedSalaryRanges.map((range) => (
                <Badge key={range} variant="secondary" className="gap-1">
                  {salaryRanges.find((r: { value: string; label: string }) => r.value === range)?.label}
                  <X
                    className="w-3 h-3 cursor-pointer"
                    onClick={() => toggleSalaryRange(range)}
                  />
                </Badge>
              ))}
              {selectedTypes.map((type) => (
                <Badge key={type} variant="secondary" className="gap-1">
                  {jobTypes.find((t) => t.value === type)?.label}
                  <X
                    className="w-3 h-3 cursor-pointer"
                    onClick={() => toggleType(type)}
                  />
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-8">
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <div className="bg-card rounded-xl p-6 border border-border shadow-sm">
                <FilterSection />
              </div>
            </div>
          </aside>

          <main className="flex-1">
            <div className="mb-6 flex items-center justify-between">
              <p className="text-muted-foreground">
                找到 <span className="font-semibold text-foreground">{filteredJobs.length}</span> 个职位
              </p>
            </div>

            {filteredJobs.length > 0 ? (
              <div className="grid gap-6">
                {filteredJobs.map((job) => (
                  <JobCard key={job.id} job={job} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
                  <Search className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  未找到匹配的职位
                </h3>
                <p className="text-muted-foreground mb-6">
                  尝试调整筛选条件或搜索关键词
                </p>
                <Button onClick={clearAllFilters}>清除所有筛选</Button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
